/**
 * Challenge Tracking Utilities
 * Tracks daily challenge progress and perfect days using localStorage
 */

import { getTodayString, checkChallengeProgress, getTodaysChallenges } from './challenges';
import { CHALLENGES, ChallengeId } from '../types/challenges';
import { SessionResult } from '../types';
import { saveDailyChallenge } from './indexedDB';

export interface LocalChallengeProgress {
  challengeId: string;
  progress: number;
  completed: boolean;
}

const getTodayKey = (): string => `flashread_challenges_${getTodayString()}`;
const getPerfectDatesKey = (): string => 'flashread_perfect_dates';

export function getTodaysChallengeProgress(): LocalChallengeProgress[] {
  const key = getTodayKey();
  const raw = localStorage.getItem(key);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      // fall through
    }
  }
  // Initialize with today's challenges
  const todayChallenges = getTodaysChallenges().challenges;
  return todayChallenges.map(id => ({ challengeId: id, progress: 0, completed: false }));
}

export function saveTodaysChallengeProgress(progress: LocalChallengeProgress[]): void {
  const key = getTodayKey();
  localStorage.setItem(key, JSON.stringify(progress));
}

export function getPerfectDates(): string[] {
  const raw = localStorage.getItem(getPerfectDatesKey());
  return raw ? JSON.parse(raw) : [];
}

export function addPerfectDate(date: string): void {
  const dates = new Set<string>(getPerfectDates());
  dates.add(date);
  localStorage.setItem(getPerfectDatesKey(), JSON.stringify([...dates]));
}

/**
 * Update challenge progress based on a completed session.
 * Returns counts of newly completed challenges and whether today became a perfect day.
 */
export async function updateChallengeProgressFromSession(
  sessionResult: SessionResult,
  currentStreak: number,
  userId?: string
): Promise<{ newlyCompleted: Array<{challengeId: string, xpEarned: number}>; newlyPerfectDay: boolean }> {
  const today = getTodayString();
  const progress = getTodaysChallengeProgress();
  const todayChallenges = getTodaysChallenges().challenges;

  let newlyCompleted: Array<{challengeId: string, xpEarned: number}> = [];
  let allCompleted = true;

  for (let i = 0; i < progress.length; i++) {
    const entry = progress[i];
    const challengeId = entry.challengeId as ChallengeId;
    const currentProgress = entry.progress;
    const alreadyCompleted = entry.completed;

    const result = checkChallengeProgress(
      challengeId,
      sessionResult,
      currentProgress,
      currentStreak
    );

    if (!alreadyCompleted && result.completed) {
      const xpReward = CHALLENGES[challengeId]?.xpReward ?? 0;
      newlyCompleted.push({ challengeId, xpEarned: xpReward });
    }
    entry.progress = result.newProgress;
    entry.completed = result.completed;
    if (!result.completed) allCompleted = false;
  }

  saveTodaysChallengeProgress(progress);

  // Save newly completed challenges to IndexedDB for sync (if userId provided)
  if (userId) {
    for (const comp of newlyCompleted) {
      await saveDailyChallenge({
        userId,
        challengeDate: today,
        challengeId: comp.challengeId,
        completed: true,
        completedAt: new Date().toISOString(),
        xpEarned: comp.xpEarned,
        synced: false,
      });
    }
  }

  // Check perfect day
  let newlyPerfectDay = false;
  const perfectDates = new Set<string>(getPerfectDates());
  if (allCompleted && !perfectDates.has(today)) {
    perfectDates.add(today);
    localStorage.setItem(getPerfectDatesKey(), JSON.stringify([...perfectDates]));
    newlyPerfectDay = true;
  }

  return { newlyCompleted, newlyPerfectDay };
}
